import styles from './index.module.css'

import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import React, { useMemo, useState } from 'react'

import {
  Button,
  Checkbox,
  DropdownMenu,
  Flex,
  Separator,
  Text,
  TextField,
} from 'components/ds'
import HighlightedText from 'components/ui/highlighted-text'

type Column = {
  id: string
  columnDef: { header: unknown; meta?: { metadataDef?: { label?: string } } }
  getIsVisible: () => boolean
  toggleVisibility: (_visible: string | boolean) => void
}

type Props = {
  columns?: any[]
}

const getColumnLabel = (column: Column): string => {
  const header = column.columnDef?.header
  if (typeof header === 'string') return header
  const metaLabel = column.columnDef?.meta?.metadataDef?.label
  if (typeof metaLabel === 'string' && metaLabel.length > 0) return metaLabel
  return String(column.id ?? '')
}

export const SearchTableColumnDropdownMenu = ({ columns }: Props) => {
  const [searchValue, setSearchValue] = useState('')

  const handleSearchColumns = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value || '')
  }

  const filteredColumns = useMemo(() => {
    if (!columns) return []

    const lowerSearch = searchValue.toLowerCase()

    return columns.filter(column => {
      if (!column.getCanHide()) return false
      return getColumnLabel(column).toLowerCase().includes(lowerSearch)
    })
  }, [columns, searchValue])

  const visibleColumns = filteredColumns.filter(col => col.getIsVisible())
  const hiddenColumns = filteredColumns.filter(col => !col.getIsVisible())

  const ColumnRow = ({
    column,
    isVisible,
  }: {
    column: Column
    isVisible: boolean
  }) => (
    <DropdownMenu.Item
      onSelect={e => {
        // prevent closing
        e.preventDefault()
      }}
      asChild
    >
      <Text size="2" as="label" style={{ width: '100%', cursor: 'pointer' }}>
        <Flex gap="2">
          <Checkbox
            checked={isVisible}
            onCheckedChange={value => {
              column.toggleVisibility(value)
            }}
          />
          <HighlightedText
            text={getColumnLabel(column)}
            highlights={searchValue ? [searchValue] : []}
            customColors={{ background: 'var(--disco-yellow)' }}
          />
        </Flex>
      </Text>
    </DropdownMenu.Item>
  )

  return (
    <DropdownMenu.Root modal={false}>
      <Flex p="3" justify="end" align="center" width="100%" height="100%">
        <DropdownMenu.Trigger>
          <Button
            variant="solid"
            size="1"
            aria-label="Columns"
            className={styles.ManageColumnsButton}
          >
            <LuPlus size={14} />
            Manage Columns
          </Button>
        </DropdownMenu.Trigger>
      </Flex>
      <DropdownMenu.Content
        style={{ minWidth: '250px', maxHeight: '400px' }}
        sideOffset={0}
        align="end"
        side="bottom"
      >
        <Flex width="100%" direction="column" p="2">
          <TextField.Root
            value={searchValue || ''}
            onChange={handleSearchColumns}
            placeholder="Search columns..."
            type="text"
          />
        </Flex>
        {visibleColumns.length > 0 && (
          <Flex direction="column" width="100%">
            {visibleColumns.map(col => (
              <ColumnRow key={col.id} column={col} isVisible />
            ))}
          </Flex>
        )}
        {hiddenColumns.length > 0 && visibleColumns.length > 0 && (
          <Separator size="4" my="2" />
        )}

        {hiddenColumns.length > 0 && (
          <Flex direction="column" width="100%">
            {hiddenColumns.map(col => (
              <ColumnRow key={col.id} column={col} isVisible={false} />
            ))}
          </Flex>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
